import { AppColumn, AppColumnMeta, AppTable } from './appTable';
import { z } from 'zod';

export type SelectionOption = 'ALL' | 'SELECTED' | 'UNSELECTED';
export type BooleanOption = 'ALL' | 'CHECKED' | 'UNCHECKED';

export interface FilterProps<TData extends object> {
  column: AppColumn<any, unknown>;
  table: AppTable<TData>;
  clientControlled?: boolean;
}

export type DataTableRef = {
  /**
   * Originals for the currently selected rows.
   *
   * Read on demand rather than pushed through React state: a selection change
   * would otherwise re-render the consuming page (and everything below it) on
   * every checkbox click. Consumers only need the rows inside event handlers.
   */
  getSelectedRows<T = any>(): T[];
  /**
   * Originals for the rows currently passing filters/sort, in display order.
   */
  getVisibleRows<T = any>(): T[];
  /**
   * Clears all selected rows.
   */
  resetRowSelection(): void;
} & DataTableFilterRef;

export type DataTableFilterLimit = Exclude<
  AppColumnMeta<any, any>['filterVariant'],
  undefined
>;

export interface DataTableFilterRef {
  /**
   * Reset the filter. By default it returns to the column's default value
   */
  resetFilters(clearToEmpty?: boolean): void;
  /**
   * Set filter value for specific column
   */
  setFilterValue(options: { columnKey: string; value: unknown }): void;
}

/**
 * Date filter comparison operators for table filtering
 *
 * BETWEEN_INCLUSIVE vs BETWEEN_EXCLUSIVE:
 * - BETWEEN_INCLUSIVE: Includes both start and end dates in the range (value >= firstDate && value <= secondDate)
 *   Example: Between 2025-01-10 and 2025-01-15 includes both Jan 10th and Jan 15th
 *
 * - BETWEEN_EXCLUSIVE: Excludes both start and end dates from the range (value > firstDate && value < secondDate)
 *   Example: Between 2025-01-10 and 2025-01-15 excludes Jan 10th and Jan 15th, only includes Jan 11-14
 *
 * Most date range filters should use BETWEEN_INCLUSIVE to include the boundary dates.
 */
export enum DateFilterComparison {
  IS_BLANK = 'IS_BLANK',
  IS_NOT_BLANK = 'IS_NOT_BLANK',
  EQUALS = 'EQUALS',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_EQUAL = 'GREATER_THAN_EQUAL',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_EQUAL = 'LESS_THAN_EQUAL',
  BETWEEN_EXCLUSIVE = 'BETWEEN_EXCLUSIVE',
  BETWEEN_INCLUSIVE = 'BETWEEN_INCLUSIVE',
}

export const DateFilterDataSchema = z.object({
  comparison: z.nativeEnum(DateFilterComparison),
  firstDate: z.string().nullish(),
  secondDate: z.string().nullish().optional(),
});

export type DateFilterData = z.infer<typeof DateFilterDataSchema>;

export function isBlankNotBlankComparison(
  comparison: DateFilterComparison,
): boolean {
  return (
    comparison === DateFilterComparison.IS_BLANK ||
    comparison === DateFilterComparison.IS_NOT_BLANK
  );
}

export function isBetweenComparison(comparison: DateFilterComparison): boolean {
  return (
    comparison === DateFilterComparison.BETWEEN_EXCLUSIVE ||
    comparison === DateFilterComparison.BETWEEN_INCLUSIVE
  );
}

export enum NumberFilterComparison {
  IS_BLANK = 'IS_BLANK',
  IS_NOT_BLANK = 'IS_NOT_BLANK',
  EQUALS = 'EQUALS',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_EQUAL = 'GREATER_THAN_EQUAL',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_EQUAL = 'LESS_THAN_EQUAL',
  BETWEEN_EXCLUSIVE = 'BETWEEN_EXCLUSIVE',
  BETWEEN_INCLUSIVE = 'BETWEEN_INCLUSIVE',
}

export const NumberFilterDataSchema = z.object({
  comparison: z.nativeEnum(NumberFilterComparison),
  firstValue: z.string().nullish(),
  secondValue: z.string().nullish().optional(),
});

export type NumberFilterData = z.infer<typeof NumberFilterDataSchema>;

export function isNumberBlankNotBlankComparison(
  comparison: NumberFilterComparison,
): boolean {
  return (
    comparison === NumberFilterComparison.IS_BLANK ||
    comparison === NumberFilterComparison.IS_NOT_BLANK
  );
}

export function isNumberBetweenComparison(
  comparison: NumberFilterComparison,
): boolean {
  return (
    comparison === NumberFilterComparison.BETWEEN_EXCLUSIVE ||
    comparison === NumberFilterComparison.BETWEEN_INCLUSIVE
  );
}

export interface OwnerFilterData {
  ownerId: string;
  ownerName: string;
}

export interface RecordSearchFilterData {
  id: string;
  label: string;
}
